import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {Container} from "@pg-design/grid-module";
import {borderRadius, flexAbsoluteCenter, mb, mt, pb, pt, pv} from "@pg-design/helpers-css";
import {BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {IRPStore} from "../../app/rp_reducer";
import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {IVariablesStore} from "../../variables/reducers/variables_reducer";
import {ViewType} from "../../view_type/ViewType";
import {IRanking} from "../actions/fetch_vendor_detail_rankings";
import {IVendorDetailArticle} from "../actions/fetch_vendor_detail_related_articles";
import {AnniversaryAwards} from "../components/detail/AnniversaryAwards";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailNavBar} from "../components/detail/VendorDetailNavBar";
import {VendorDetailOfferRanking} from "../components/detail/VendorDetailOfferRanking";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorDetailSubHeader} from "../components/detail/VendorDetailSubHeader";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {IVendorDetailStore} from "../reducers/vendor_detail_reducer";
import {getActiveVendorOffers} from "../utils/get_active_vendor_offers";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

interface IStateProps {
    vendorDetail: IVendorDetailStore;
    variables: IVariablesStore;
    rankings: IRanking[];
    articles: IVendorDetailArticle[];
    appliedOffers: number[];
    favouriteOffers: number[];
}

interface IActionProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionProps, IOwnProps {}

const VendorDetailC: React.FC<IProps> = (props) => {
    const {vendor} = props.vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const soldOffers = props.vendorDetail.offers.filter((offer) => offer.configuration.display_type === OfferDisplayType.SOLD);
    const activeFlatsAndHouses = getActiveVendorOffers(props.vendorDetail.offers);
    const activeFlats = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer));
    const activeHouses = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer));
    const activeCommercials = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer));
    const activeFlatsAndHousesInRegion = props.vendorDetail.offersInRegion.filter((offer) => offer.type !== OfferType.COMMERCIAL && isActiveVendorOffer(offer));

    const navBarRef = useRef<HTMLDivElement>(null);
    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refAwards = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refAboutUs, refAwards, refPromotions, refOffers, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const hasPromotions = !!vendor && vendor.promotions?.length > 0;
    const hasAwards = props.rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses.length > 0;

    const getElems = useCallback(() => {
        const res = [];
        res.push("about_us");
        if (hasAwards) {
            res.push("awards");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }
        res.push("contact");
        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "awards":
                    return hasAwards;
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;
            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const openMapBtnClick = () => setIsMapModalOpen(true);

    const mapTriggerButtonStyled = (
        <MapTriggerButtonStyled
            iconLeft={BrandMapPinIcon}
            variant="brand_icon"
            size="big"
            onClick={() => {
                openMapBtnClick();
            }}
        >
            Zobacz na mapie
        </MapTriggerButtonStyled>
    );

    const isSticky = useStickyIntersection(navBarRef);

    if (!vendor) {
        return (
            <div css={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <Container>
                    <VendorBreadcrumbs />
                </Container>

                <VendorContainer>
                    <div css={[pt(4), pb(2)]}>
                        <VendorDetailHeader vendor={vendor} />
                    </div>
                </VendorContainer>

                <div css={[navigationWrapper(isSticky)]} ref={navBarRef}>
                    <VendorContainer>
                        <VendorDetailNavBar
                            refElements={refElems}
                            visibleElement={visibleElem}
                            hasPromotions={hasPromotions}
                            hasActiveFlatsAndHouses={hasActiveFlatsAndHouses}
                            hasAwards={hasAwards}
                        />
                    </VendorContainer>
                </div>

                <VendorContainer>
                    <section id="o-nas" css={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={props.variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    {hasActiveFlatsAndHouses && (
                        <section id="oferta" css={activeOffersSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <>
                                <div css={activeOffersHeaderHolder}>
                                    <VendorDetailSubHeaderStyled vendor={vendor} css={[activeOfferHeaderStyle]} />

                                    {mapTriggerButtonStyled}
                                </div>

                                <VendorDetailActiveOffers
                                    offersInRegionRequest={props.vendorDetail.offersInRegionRequest}
                                    offers={activeFlatsAndHouses}
                                    vendor={vendor}
                                    cities={props.vendorDetail.cities}
                                    openMapBtnClick={() => setIsMapModalOpen(true)}
                                    offersInRegion={activeFlatsAndHousesInRegion}
                                />
                            </>
                        </section>
                    )}

                    {hasAwards && (
                        <section id="nagrody" css={awardsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refAwards} />
                            <>
                                <VendorDetailOfferRanking vendor={vendor} rankings={props.rankings} />

                                {vendorAward && <AnniversaryAwards vendorName={vendor.name} vendorId={vendor.id} />}
                            </>
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" css={promotionsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={vendor.promotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {activeCommercials.length > 0 && (
                        <section css={commercialsSectionStyle}>
                            <VendorDetailCommercials offers={activeCommercials} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {soldOffers.length > 0 && (
                        <section css={soldOffersSectionStyle}>
                            <VendorDetailSoldOffers offers={soldOffers} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <section id="kontakt" css={contactSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>
            {props.articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={props.articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={props.favouriteOffers}
                appliedOffers={props.appliedOffers}
            />
        </article>
    );
};

export const VendorDetailView = connect<IStateProps, IActionProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(VendorDetailC);

/**
 * Connect
 */
function mapStateToProps(store: IRPStore): IStateProps {
    return {
        vendorDetail: store.vendor.detail,
        variables: store.variables,
        articles: store.vendor.detail.articles,
        rankings: store.vendor.detail.rankings,
        favouriteOffers: store.favourite.favourites.offers,
        appliedOffers: store.offerListMap.appliedOffers.data
    };
}

function mapActionsToProps(): IActionProps {
    return {};
}

const loaderWrapper = css`
    ${borderRadius(1)};
    ${flexAbsoluteCenter};
    height: 400px;
`;

const VendorDetailSubHeaderStyled = styled(VendorDetailSubHeader)`
    flex: 0 0 100%;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        align-self: center;
        flex: 1 1 50%;
    }
`;

const MapTriggerButtonStyled = styled(Button)`
    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex: 0 0 auto;
        order: 2;
    }
`;

const navigationWrapperSticky = (theme: Theme) => css`
    background: ${theme.colors.gray["200"]};
    ${elevation(4)}
`;

const navigationWrapperNonSticky = (theme: Theme) => css`
    background: ${theme.colors.gray["100"]};
`;

const navigationWrapper = (isSticky: boolean) => (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        position: sticky;
        top: 0;
        z-index: 1000;
        ${pv(2)};

        ${isSticky ? navigationWrapperSticky(theme) : navigationWrapperNonSticky(theme)};
    }
`;

const generalSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt(6)};
        ${mb(7)};
    }
`;

const awardsSectionStyle = (theme: Theme) => css`
    ${mb(8)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)};
    }
`;

const promotionsSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)};
    }
`;

const activeOffersHeaderHolder = (theme: Theme) => css`
    text-align: center;
    ${mb(8)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
        text-align: left;
        ${mb(1.5)};
    }
`;

const activeOfferHeaderStyle = (theme: Theme) => css`
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(0)};
    }
`;

const activeOffersSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const commercialsSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const soldOffersSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const contactSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(5)};
    }
`;
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {Container} from "@pg-design/grid-module";
import {borderRadius, flexAbsoluteCenter, mb, mt, pb, pt, pv} from "@pg-design/helpers-css";
import {BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {IRPStore} from "../../app/rp_reducer";
import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {IVariablesStore} from "../../variables/reducers/variables_reducer";
import {ViewType} from "../../view_type/ViewType";
import {IRanking} from "../actions/fetch_vendor_detail_rankings";
import {IVendorDetailArticle} from "../actions/fetch_vendor_detail_related_articles";
import {AnniversaryAwards} from "../components/detail/AnniversaryAwards";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailNavBar} from "../components/detail/VendorDetailNavBar";
import {VendorDetailOfferRanking} from "../components/detail/VendorDetailOfferRanking";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorDetailSubHeader} from "../components/detail/VendorDetailSubHeader";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {IVendorDetailStore} from "../reducers/vendor_detail_reducer";
import {getActiveVendorOffers} from "../utils/get_active_vendor_offers";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

interface IStateProps {
    vendorDetail: IVendorDetailStore;
    variables: IVariablesStore;
    rankings: IRanking[];
    articles: IVendorDetailArticle[];
    appliedOffers: number[];
    favouriteOffers: number[];
}

interface IActionProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionProps, IOwnProps {}

const VendorDetailC: React.FC<IProps> = (props) => {
    const {vendor} = props.vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const soldOffers = props.vendorDetail.offers.filter((offer) => offer.configuration.display_type === OfferDisplayType.SOLD);
    const activeFlatsAndHouses = getActiveVendorOffers(props.vendorDetail.offers);
    const activeFlats = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer));
    const activeHouses = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer));
    const activeCommercials = props.vendorDetail.offers.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer));
    const activeFlatsAndHousesInRegion = props.vendorDetail.offersInRegion.filter((offer) => offer.type !== OfferType.COMMERCIAL && isActiveVendorOffer(offer));

    const navBarRef = useRef<HTMLDivElement>(null);
    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refAwards = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refAboutUs, refAwards, refPromotions, refOffers, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const hasPromotions = !!vendor && vendor.promotions?.length > 0;
    const hasAwards = props.rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses.length > 0;

    const getElems = useCallback(() => {
        const res = [];
        res.push("about_us");
        if (hasAwards) {
            res.push("awards");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }
        res.push("contact");
        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "awards":
                    return hasAwards;
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;
            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const openMapBtnClick = () => setIsMapModalOpen(true);

    const mapTriggerButtonStyled = (
        <MapTriggerButtonStyled
            iconLeft={BrandMapPinIcon}
            variant="brand_icon"
            size="big"
            onClick={() => {
                openMapBtnClick();
            }}
        >
            Zobacz na mapie
        </MapTriggerButtonStyled>
    );

    const isSticky = useStickyIntersection(navBarRef);

    if (!vendor) {
        return (
            <div css={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <Container>
                    <VendorBreadcrumbs />
                </Container>

                <VendorContainer>
                    <div css={[pt(4), pb(2)]}>
                        <VendorDetailHeader vendor={vendor} />
                    </div>
                </VendorContainer>

                <div css={[navigationWrapper(isSticky)]} ref={navBarRef}>
                    <VendorContainer>
                        <VendorDetailNavBar
                            refElements={refElems}
                            visibleElement={visibleElem}
                            hasPromotions={hasPromotions}
                            hasActiveFlatsAndHouses={hasActiveFlatsAndHouses}
                            hasAwards={hasAwards}
                        />
                    </VendorContainer>
                </div>

                <VendorContainer>
                    <section id="o-nas" css={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={props.variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    {hasActiveFlatsAndHouses && (
                        <section id="oferta" css={activeOffersSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <>
                                <div css={activeOffersHeaderHolder}>
                                    <VendorDetailSubHeaderStyled vendor={vendor} css={[activeOfferHeaderStyle]} />

                                    {mapTriggerButtonStyled}
                                </div>

                                <VendorDetailActiveOffers
                                    offersInRegionRequest={props.vendorDetail.offersInRegionRequest}
                                    offers={activeFlatsAndHouses}
                                    vendor={vendor}
                                    cities={props.vendorDetail.cities}
                                    openMapBtnClick={() => setIsMapModalOpen(true)}
                                    offersInRegion={activeFlatsAndHousesInRegion}
                                />
                            </>
                        </section>
                    )}

                    {hasAwards && (
                        <section id="nagrody" css={awardsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refAwards} />
                            <>
                                <VendorDetailOfferRanking vendor={vendor} rankings={props.rankings} />

                                {vendorAward && <AnniversaryAwards vendorName={vendor.name} vendorId={vendor.id} />}
                            </>
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" css={promotionsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={vendor.promotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {activeCommercials.length > 0 && (
                        <section css={commercialsSectionStyle}>
                            <VendorDetailCommercials offers={activeCommercials} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {soldOffers.length > 0 && (
                        <section css={soldOffersSectionStyle}>
                            <VendorDetailSoldOffers offers={soldOffers} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <section id="kontakt" css={contactSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>
            {props.articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={props.articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={props.favouriteOffers}
                appliedOffers={props.appliedOffers}
            />
        </article>
    );
};

export const VendorDetailView = connect<IStateProps, IActionProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(VendorDetailC);

/**
 * Connect
 */
function mapStateToProps(store: IRPStore): IStateProps {
    return {
        vendorDetail: store.vendor.detail,
        variables: store.variables,
        articles: store.vendor.detail.articles,
        rankings: store.vendor.detail.rankings,
        favouriteOffers: store.favourite.favourites.offers,
        appliedOffers: store.offerListMap.appliedOffers.data
    };
}

function mapActionsToProps(): IActionProps {
    return {};
}

const loaderWrapper = css`
    ${borderRadius(1)};
    ${flexAbsoluteCenter};
    height: 400px;
`;

const VendorDetailSubHeaderStyled = styled(VendorDetailSubHeader)`
    flex: 0 0 100%;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        align-self: center;
        flex: 1 1 50%;
    }
`;

const MapTriggerButtonStyled = styled(Button)`
    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex: 0 0 auto;
        order: 2;
    }
`;

const navigationWrapperSticky = (theme: Theme) => css`
    background: ${theme.colors.gray["200"]};
    ${elevation(4)}
`;

const navigationWrapperNonSticky = (theme: Theme) => css`
    background: ${theme.colors.gray["100"]};
`;

const navigationWrapper = (isSticky: boolean) => (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        position: sticky;
        top: 0;
        z-index: 1000;
        ${pv(2)};

        ${isSticky ? navigationWrapperSticky(theme) : navigationWrapperNonSticky(theme)};
    }
`;

const generalSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt(6)};
        ${mb(7)};
    }
`;

const awardsSectionStyle = (theme: Theme) => css`
    ${mb(8)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)};
    }
`;

const promotionsSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)};
    }
`;

const activeOffersHeaderHolder = (theme: Theme) => css`
    text-align: center;
    ${mb(8)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
        text-align: left;
        ${mb(1.5)};
    }
`;

const activeOfferHeaderStyle = (theme: Theme) => css`
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(0)};
    }
`;

const activeOffersSectionStyle = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const commercialsSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const soldOffersSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(8)};
    }
`;

const contactSectionStyle = (theme: Theme) => css`
    ${mb(6)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(5)};
    }
`;
